import React, { useEffect, useState } from "react";
import { MapkitContext } from "react-mapkit";

const MAPKIT_SRC = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";
let loadPromise;

const loadMapkit = (tokenUrl) => {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    const done = () => {
      window.mapkit.init({
        authorizationCallback: (cb) => {
          fetch(tokenUrl)
            .then((res) => res.text())
            .then(cb);
        },
      });
      resolve(window.mapkit);
    };
    if (window.mapkit) return done();
    const s = document.createElement("script");
    s.src = MAPKIT_SRC;
    s.crossOrigin = "anonymous";
    s.async = true;
    s.onload = done;
    s.onerror = () => {
      loadPromise = undefined;
      reject(new Error("MapKit failed to load"));
    };
    document.head.appendChild(s);
  });
  return loadPromise;
};

/**
 * Drop-in replacement for react-mapkit's MapkitProvider that postpones
 * downloading mapkit.js. On phones it waits for the first user interaction
 * (or a fallback timeout); on larger screens it loads once the browser is idle.
 * Provides the same MapkitContext, so useMap()/<Map> keep working.
 */
const LazyMapkitProvider = ({ tokenOrCallback, children }) => {
  const [context, setContext] = useState({
    mapkit: undefined,
    isInProvider: true,
  });

  useEffect(() => {
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      cleanup();
      loadMapkit(tokenOrCallback)
        .then((mapkit) => setContext({ mapkit, isInProvider: true }))
        .catch(() => {});
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    const opts = { capture: true, passive: true, once: true };
    let timer;
    function cleanup() {
      events.forEach((e) => window.removeEventListener(e, start, opts));
      clearTimeout(timer);
    }
    const isPhone = window.matchMedia("(max-width: 768px)").matches;
    if (isPhone) {
      events.forEach((e) => window.addEventListener(e, start, opts));
      timer = setTimeout(start, 8000);
    } else {
      timer = setTimeout(start, 1500);
    }
    return cleanup;
  }, [tokenOrCallback]);

  return (
    <MapkitContext.Provider value={context}>{children}</MapkitContext.Provider>
  );
};

export default LazyMapkitProvider;
